import React from 'react';
import { classNames } from '../utils/classNames';
import { RANK_MAX, RANK_TICKS, RankValue } from './rankScale';

// A line chart of places in a list, one value per date.
// x: a calendar scale (a gap of 3 days is 3 times wider than a gap of 1 day), with month labels.
// y: a log scale, place 1 at the top, ticks 1, 10, 25, 50, 100, 250 and 500, and a gray band
// under 500 for the value 'over'. A null value is a gap in the line. A value with no
// neighbour is a dot.
// The lines: one or more lines in front (emphasis: 3 px with a white halo), the other lines
// thin (1.25 px, 85% opacity), an optional dashed reference line, an optional light band
// between two value rows, and an optional row of small marks under the chart.
// A pointer or the arrow keys on a date open a tooltip with all the values of that date.
// A line can give a text for each gap (gapLabels), for example "sold out"; the tooltip shows it.
// Below 640 px of width, the y axis stays fixed and the plot scrolls sideways.
// The chart knows nothing about the data: the caller gives the labels, the colours and the text.

export type RankLineSeries = {
	id: string,
	label: string,
	values: RankValue[],
	// A CSS colour of the line, for example from RANK_LINE_COLORS.
	color: string,
	emphasis?: boolean,
	dashed?: boolean,
	// The tooltip text of each gap (a null value), one per date, for example "sold out".
	// A gap without a text shows the text of formatValue(null) ('–' by default).
	gapLabels?: (string | null)[],
};

export type RankLineReference = {
	label: string,
	values: RankValue[],
	// A CSS colour. Without it, the line is slate-500.
	color?: string,
};

export type RankLineBand = {
	label: string,
	lo: RankValue[],
	hi: RankValue[],
};

export type RankLineMarkers = {
	label: string,
	flags: boolean[],
};

type Props = {
	// ISO dates (YYYY-MM-DD), in ascending order. Each values row has one value per date.
	dates: string[],
	series: RankLineSeries[],
	reference?: RankLineReference,
	band?: RankLineBand,
	markers?: RankLineMarkers,
	// The name of the chart for a screen reader.
	ariaLabel: string,
	// The y label of the band for 'over', and the tooltip text of 'over'.
	overLabel?: string,
	// The text under the chart below 640 px, for example "Swipe the chart to see all months.".
	scrollHint?: React.ReactNode,
	// The text in the plot when no line has a value.
	emptyLabel?: React.ReactNode,
	// The locale of the month and date labels.
	locale?: string,
	formatDate?: (iso: string) => string,
	formatValue?: (value: RankValue) => string,
	// The width of the plot below 640 px, in px.
	narrowPlotWidth?: number,
};

const NARROW = 640;
const DAY_MS = 86_400_000;
const FONT = 11;
const LINE_W = 1.25;
const EMPHASIS_W = 3;
const REFERENCE_W = 1.5;
const GRID = 'stroke-slate-200/60';

const dayOf = (iso: string): number => {
	const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
	return Date.UTC(y, m - 1, d) / DAY_MS;
};

// About the width of a text of 11 px.
const textWidth = (text: string): number => text.length * 6.2;

type Geo = {
	W: number, left: number, right: number,
	top: number, pb: number, band0: number, band1: number, yb: number, ys: number,
	bottom: number, xlab: number, H: number,
	d0: number, d1: number, days: number[],
};

const makeGeo = (W: number, left: number, right: number, days: number[], hasMarkers: boolean): Geo => {
	const top = 12;
	const pb = 292;
	const band0 = pb + 8;
	const band1 = pb + 34;
	const strip1 = band1 + 20;
	const bottom = hasMarkers ? strip1 : band1;
	const xlab = bottom + 20;
	return {
		W, left, right, top, pb, band0, band1, yb: (band0 + band1) / 2, ys: band1 + 13,
		bottom, xlab, H: xlab + 8,
		d0: days.length > 0 ? days[0] : 0, d1: days.length > 0 ? days[days.length - 1] : 0, days,
	};
};

const xOf = (g: Geo, day: number): number => {
	const span = g.W - g.left - g.right;
	if (g.d1 === g.d0) return g.left + span / 2;
	return g.left + (day - g.d0) / (g.d1 - g.d0) * span;
};

const yOfPlace = (g: Geo, place: number): number =>
	g.top + Math.log10(Math.max(place, 1)) / Math.log10(RANK_MAX) * (g.pb - g.top);

const yOf = (g: Geo, value: RankValue | undefined): number | null => {
	if (value === null || value === undefined) return null;
	if (value === 'over' || value > RANK_MAX) return g.yb;
	return yOfPlace(g, value);
};

type Pt = [number, number];

const segmentsOf = (g: Geo, values: RankValue[]): Pt[][] => {
	const out: Pt[][] = [];
	let cur: Pt[] = [];
	g.days.forEach((day, i) => {
		const y = yOf(g, values[i]);
		if (y === null) {
			if (cur.length > 0) out.push(cur);
			cur = [];
		} else cur.push([xOf(g, day), y]);
	});
	if (cur.length > 0) out.push(cur);
	return out;
};

const points = (pts: Pt[]): string => pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

// A colour from the caller, or Tailwind classes for the stroke and the fill when the caller gives no colour.
type Paint = { color?: string, stroke?: string, fill?: string };

// One line: its segments, and a dot for a segment of one point.
const Line: React.FunctionComponent<{ g: Geo, values: RankValue[], paint: Paint, width: number, dash?: string, halo?: boolean }> = ({
	g, values, paint, width, dash, halo = false,
}) => {
	const segs = segmentsOf(g, values);
	const r = width > 2 ? 2.6 : 2;
	return <g>
		{halo && segs.filter(s => s.length > 1).map((s, i) =>
			<polyline key={`h${i}`} points={points(s)} fill="none" className="stroke-white" strokeWidth={width + 4}
				strokeLinejoin="round" strokeLinecap="round" />)}
		{segs.map((s, i) => s.length === 1
			? <circle key={i} cx={s[0][0]} cy={s[0][1]} r={r + (halo ? 1 : 0)} fill={paint.color}
				className={classNames(paint.color === undefined ? (paint.fill ?? '') : '', halo ? 'stroke-white' : '')}
				strokeWidth={halo ? 2 : undefined} />
			: <polyline key={i} points={points(s)} fill="none" stroke={paint.color} className={paint.color === undefined ? paint.stroke : undefined}
				strokeWidth={width} strokeDasharray={dash} strokeLinejoin="round" strokeLinecap="round" />)}
	</g>;
};

// The light area between two value rows, cut where a row has no value.
const Band: React.FunctionComponent<{ g: Geo, lo: RankValue[], hi: RankValue[] }> = ({ g, lo, hi }) => {
	const polys: Pt[][] = [];
	let a: Pt[] = [];
	let b: Pt[] = [];
	const flush = () => {
		if (a.length > 1) polys.push([...a, ...b.reverse()]);
		a = [];
		b = [];
	};
	g.days.forEach((day, i) => {
		const y0 = yOf(g, lo[i]);
		const y1 = yOf(g, hi[i]);
		if (y0 === null || y1 === null) {
			flush();
			return;
		}
		const x = xOf(g, day);
		a.push([x, y0]);
		b.push([x, y1]);
	});
	flush();
	return <g>{polys.map((p, i) => <polygon key={i} points={points(p)} className="fill-slate-300" fillOpacity={0.45} />)}</g>;
};

const Swatch: React.FunctionComponent<{ kind: 'line' | 'band' | 'mark', paint?: Paint, width?: number, dash?: string }> = ({ kind, paint, width = 2, dash }) =>
	<svg width="16" height="10" viewBox="0 0 16 10" aria-hidden="true" className="shrink-0">
		{kind === 'line' && <line x1="1" y1="5" x2="15" y2="5" stroke={paint?.color} className={paint?.color === undefined ? paint?.stroke : undefined}
			strokeWidth={width} strokeDasharray={dash} strokeLinecap="round" />}
		{kind === 'band' && <rect x="1" y="1" width="14" height="8" rx="2" className="fill-slate-300" fillOpacity={0.7} />}
		{kind === 'mark' && <rect x="5.5" y="1" width="5" height="8" rx="1" className="fill-blue-900" />}
	</svg>;

const RankLineChart: React.FunctionComponent<Props> = ({
	dates,
	series,
	reference,
	band,
	markers,
	ariaLabel,
	overLabel = '500+',
	scrollHint,
	emptyLabel,
	locale = 'en-GB',
	formatDate,
	formatValue,
	narrowPlotWidth = 900,
}) => {
	const wrapRef = React.useRef<HTMLDivElement>(null);
	const scrollRef = React.useRef<HTMLDivElement>(null);
	const tipId = `rank-tip-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
	const [width, setWidth] = React.useState<number | null>(null);
	const [active, setActive] = React.useState<number | null>(null);
	const [scrollLeft, setScrollLeft] = React.useState(0);
	// The width of the open tooltip, to keep the tooltip inside the chart (a narrow chart has little room).
	const tipRef = React.useRef<HTMLDivElement>(null);
	const [tipW, setTipW] = React.useState<number | null>(null);
	React.useLayoutEffect(() => {
		setTipW(active === null || tipRef.current === null ? null : tipRef.current.getBoundingClientRect().width);
	}, [active, width]);

	React.useLayoutEffect(() => {
		const el = wrapRef.current;
		if (el === null) return;
		setWidth(el.getBoundingClientRect().width);
		if (typeof ResizeObserver === 'undefined') return;
		const ro = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const fmt = React.useMemo(() => ({
		month: new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }),
		monthYear: new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }),
		dayMonth: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }),
		weekday: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }),
		dayMonthYear: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }),
	}), [locale]);
	// "Thu 5 Nov 2026" (en-GB), without the comma that Intl puts after the weekday.
	const dateText = (iso: string) => {
		if (formatDate !== undefined) return formatDate(iso);
		const ms = dayOf(iso) * DAY_MS;
		return `${fmt.weekday.format(ms)} ${fmt.dayMonthYear.format(ms)}`;
	};
	const valueText = (value: RankValue | undefined): string => {
		const v = value ?? null;
		if (formatValue !== undefined) return formatValue(v);
		if (v === null) return '–';
		if (v === 'over' || v > RANK_MAX) return overLabel;
		return String(Math.round(v));
	};
	// The tooltip text of one line on one date: the gap text of the caller for a gap, else the value.
	const seriesText = (s: RankLineSeries, i: number): string => {
		const gap = s.values[i] === null || s.values[i] === undefined ? s.gapLabels?.[i] : undefined;
		return gap !== undefined && gap !== null && gap !== '' ? gap : valueText(s.values[i]);
	};

	const days = React.useMemo(() => dates.map(dayOf), [dates]);
	const hasMarkers = markers !== undefined;
	const hasData = series.some(s => s.values.some(v => v !== null && v !== undefined))
		|| (reference?.values.some(v => v !== null && v !== undefined) ?? false);

	const narrow = width !== null && width < NARROW;
	const labelW = Math.max(textWidth('500'), textWidth(overLabel), hasMarkers ? textWidth(markers.label) : 0);
	const axisW = Math.max(54, Math.ceil(labelW) + 8);
	const wideLeft = Math.max(64, Math.ceil(labelW) + 14);
	const W = narrow ? narrowPlotWidth : Math.max(width ?? 1112, 320);
	const g = makeGeo(W, narrow ? 6 : wideLeft, 14, days, hasMarkers);
	const x0 = g.left;
	const x1 = g.W - g.right;
	const xs = days.map(day => xOf(g, day));

	// ---- the y labels (in the wide svg, or in the fixed axis svg) ----
	const axisLabels = (xLab: number) => <g className="fill-gray-500" fontSize={FONT}>
		{RANK_TICKS.map(t => <text key={t} x={xLab} y={yOfPlace(g, t) + 4} textAnchor="end">{t}</text>)}
		<text x={xLab} y={g.yb + 4} textAnchor="end">{overLabel}</text>
		{hasMarkers && <text x={xLab} y={g.ys + 4} textAnchor="end">{markers.label}</text>}
	</g>;

	// ---- the month gridlines and labels, and the last date at the right ----
	const monthMarks = () => {
		if (days.length < 2 || g.d1 === g.d0) return null;
		const lines: React.ReactNode[] = [];
		const labels: React.ReactNode[] = [];
		const endText = fmt.dayMonth.format(g.d1 * DAY_MS);
		const xEnd = xOf(g, g.d1);
		const endLeft = xEnd - textWidth(endText) - 6;
		const start = new Date(g.d0 * DAY_MS);
		let y = start.getUTCFullYear();
		let m = start.getUTCMonth();
		let labelled = 0;
		while (Date.UTC(y, m, 1) / DAY_MS <= g.d1) {
			const first = Date.UTC(y, m, 1) / DAY_MS;
			const next = Date.UTC(y, m + 1, 1) / DAY_MS;
			if (first > g.d0) {
				const xm = xOf(g, first);
				lines.push(<line key={`m${first}`} x1={xm} y1={g.top} x2={xm} y2={g.bottom} className="stroke-gray-200" strokeWidth={1} />);
			}
			const a = Math.max(first, g.d0);
			const b = Math.min(next - 1, g.d1);
			if (b - a >= 10) {
				const text = (labelled === 0 || m === 0 ? fmt.monthYear : fmt.month).format(first * DAY_MS);
				const xc = (xOf(g, a) + xOf(g, b)) / 2;
				if (xc + textWidth(text) / 2 < endLeft) {
					labels.push(<text key={`l${first}`} x={xc} y={g.xlab} textAnchor="middle" className="fill-gray-600">{text}</text>);
					labelled += 1;
				}
			}
			m += 1;
			if (m > 11) {
				m = 0;
				y += 1;
			}
		}
		return <>
			{lines}
			<g fontSize={FONT}>
				{labels}
				<text x={xEnd} y={g.xlab} textAnchor="end" className="fill-gray-500">{endText}</text>
			</g>
		</>;
	};

	const front = series.filter(s => s.emphasis === true);
	const back = series.filter(s => s.emphasis !== true);
	const refPaint: Paint = { color: reference?.color, stroke: 'stroke-slate-500', fill: 'fill-slate-500' };

	const select = (i: number | null) => {
		setActive(i);
		if (i === null || !narrow) return;
		const box = scrollRef.current;
		if (box === null) return;
		const x = xs[i];
		if (x < box.scrollLeft + 16 || x > box.scrollLeft + box.clientWidth - 16) box.scrollLeft = x - box.clientWidth / 2;
	};
	const onKeyDown = (e: React.KeyboardEvent) => {
		const n = dates.length;
		if (n === 0) return;
		let next: number | null | undefined;
		if (e.key === 'ArrowRight') next = active === null ? 0 : Math.min(active + 1, n - 1);
		else if (e.key === 'ArrowLeft') next = active === null ? n - 1 : Math.max(active - 1, 0);
		else if (e.key === 'Home') next = 0;
		else if (e.key === 'End') next = n - 1;
		else if (e.key === 'Escape') next = null;
		if (next === undefined) return;
		e.preventDefault();
		select(next);
	};

	const plot = <>
		{RANK_TICKS.map(t => <line key={t} x1={x0} y1={yOfPlace(g, t)} x2={x1} y2={yOfPlace(g, t)} className={GRID} strokeWidth={1} />)}
		<rect x={x0} y={g.band0} width={x1 - x0} height={g.band1 - g.band0} className="fill-gray-100" />
		<line x1={x0} y1={g.band0} x2={x1} y2={g.band0} className="stroke-gray-300" strokeWidth={1} />
		{hasMarkers && <line x1={x0} y1={g.ys} x2={x1} y2={g.ys} className={GRID} strokeWidth={1} />}
		{monthMarks()}
		{band !== undefined && <Band g={g} lo={band.lo} hi={band.hi} />}
		<g opacity={0.85}>
			{[...back].reverse().map(s => <Line key={s.id} g={g} values={s.values} paint={{ color: s.color }} width={LINE_W}
				dash={s.dashed === true ? '5 4' : undefined} />)}
		</g>
		{reference !== undefined && <Line g={g} values={reference.values} paint={refPaint} width={REFERENCE_W} dash="4 3" />}
		{front.map(s => <Line key={s.id} g={g} values={s.values} paint={{ color: s.color }} width={EMPHASIS_W}
			dash={s.dashed === true ? '5 4' : undefined} halo />)}
		{hasMarkers && days.map((day, i) => markers.flags[i] === true
			? <rect key={i} x={xOf(g, day) - 2.5} y={g.ys - 4} width={5} height={8} rx={1} className="fill-blue-900" />
			: null)}
		{active !== null && active < days.length && <g pointerEvents="none">
			<line x1={xs[active]} y1={g.top} x2={xs[active]} y2={g.bottom} className="stroke-gray-400" strokeWidth={1} />
			{[...back, ...front].map(s => {
				const y = yOf(g, s.values[active]);
				return y === null ? null : <circle key={s.id} cx={xs[active]} cy={y} r={s.emphasis === true ? 4.5 : 3.5}
					fill={s.color} className="stroke-white" strokeWidth={1.5} />;
			})}
			{reference !== undefined && (() => {
				const y = yOf(g, reference.values[active]);
				return y === null ? null : <circle cx={xs[active]} cy={y} r={3.5} fill={reference.color}
					className={classNames(reference.color === undefined ? 'fill-slate-500' : '', 'stroke-white')} strokeWidth={1.5} />;
			})()}
		</g>}
		{days.map((_, i) => {
			const a = i === 0 ? x0 : (xs[i - 1] + xs[i]) / 2;
			const b = i === xs.length - 1 ? x1 : (xs[i] + xs[i + 1]) / 2;
			return <rect key={i} x={a} y={g.top} width={Math.max(b - a, 0)} height={g.bottom - g.top} fill="transparent"
				onPointerEnter={() => select(i)} onPointerDown={() => select(i)} />;
		})}
	</>;

	// ---- the tooltip of the active date ----
	const wrapW = width ?? W;
	const tipX = active === null ? 0 : (narrow ? axisW + xs[active] - scrollLeft : xs[active]);
	// The tooltip opens at the right of the date in the left half, else at the left. When its width is
	// known, it moves back inside the chart: it never goes past the left or the right edge.
	const tipStyle: React.CSSProperties = tipW === null
		? (tipX < wrapW / 2 ? { left: tipX + 12 } : { right: wrapW - tipX + 12 })
		: { left: Math.min(Math.max(tipX < wrapW / 2 ? tipX + 12 : tipX - 12 - tipW, 0), Math.max(wrapW - tipW, 0)) };
	const tooltip = active !== null && active < dates.length && <div ref={tipRef} id={tipId} role="tooltip"
		style={{ ...tipStyle, maxWidth: Math.min(320, wrapW) }}
		className="pointer-events-none absolute top-1 z-10 w-max max-w-xs rounded-lg bg-white px-3 py-2 text-xs shadow-lg ring-1 ring-black/5">
		<p className="font-semibold text-gray-900">{dateText(dates[active])}</p>
		<ul className="mt-1 flex flex-col gap-y-0.5">
			{front.map(s => <TipRow key={s.id} swatch={<Swatch kind="line" paint={{ color: s.color }} width={EMPHASIS_W} dash={s.dashed === true ? '5 4' : undefined} />}
				label={s.label} value={seriesText(s, active)} strong />)}
			{reference !== undefined && <TipRow swatch={<Swatch kind="line" paint={refPaint} width={REFERENCE_W} dash="4 3" />}
				label={reference.label} value={valueText(reference.values[active])} />}
			{band !== undefined && <TipRow swatch={<Swatch kind="band" />} label={band.label}
				value={band.lo[active] === null || band.lo[active] === undefined ? valueText(null) : `${valueText(band.lo[active])}–${valueText(band.hi[active])}`} />}
			{back.map(s => <TipRow key={s.id} swatch={<Swatch kind="line" paint={{ color: s.color }} width={2} dash={s.dashed === true ? '3 2' : undefined} />}
				label={s.label} value={seriesText(s, active)} />)}
			{hasMarkers && markers.flags[active] === true && <TipRow swatch={<Swatch kind="mark" />} label={markers.label} value="" />}
		</ul>
	</div>;

	const empty = !hasData && emptyLabel !== undefined && <div
		style={{ left: narrow ? axisW : x0, top: g.top, width: narrow ? Math.max(wrapW - axisW, 0) : x1 - x0, height: g.pb - g.top }}
		className="pointer-events-none absolute flex items-center justify-center text-sm text-gray-500">
		{emptyLabel}
	</div>;

	const focusClass = 'rounded focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600';
	const interactive = {
		tabIndex: dates.length > 0 ? 0 : -1,
		onKeyDown,
		onBlur: () => setActive(null),
		onMouseLeave: () => setActive(null),
		'aria-describedby': active !== null ? tipId : undefined,
	};

	return <div ref={wrapRef} className="relative w-full">
		{narrow
			? <div className="flex items-start">
				<svg width={axisW} height={g.H} viewBox={`0 0 ${axisW} ${g.H}`} aria-hidden="true" className="block shrink-0">
					{axisLabels(axisW - 4)}
				</svg>
				<div ref={scrollRef} className={classNames('min-w-0 flex-1 overflow-x-auto', focusClass)}
					onScroll={e => setScrollLeft(e.currentTarget.scrollLeft)} {...interactive}>
					<svg width={g.W} height={g.H} viewBox={`0 0 ${g.W} ${g.H}`} role="img" aria-label={ariaLabel} className="block">
						{plot}
					</svg>
				</div>
			</div>
			: <div className={focusClass} {...interactive}>
				<svg width={g.W} height={g.H} viewBox={`0 0 ${g.W} ${g.H}`} role="img" aria-label={ariaLabel}
					className={classNames('block', width === null ? 'w-full' : '')}>
					{axisLabels(g.left - 10)}
					{plot}
				</svg>
			</div>}
		{empty}
		{tooltip}
		{narrow && scrollHint !== undefined && <p className="mt-1.5 text-xs text-gray-500">{scrollHint}</p>}
	</div>;
};

const TipRow: React.FunctionComponent<{ swatch: React.ReactNode, label: string, value: string, strong?: boolean }> = ({ swatch, label, value, strong = false }) =>
	<li className="flex items-center gap-x-2 text-gray-600">
		{swatch}
		<span className={classNames('min-w-0 flex-1 truncate', strong ? 'font-semibold text-gray-900' : '')}>{label}</span>
		<span className="pl-3 font-medium tabular-nums text-gray-900">{value}</span>
	</li>;

export default RankLineChart;
